import { z } from 'zod';
import { LAKE_CHART_TABLES, LAKE_LOOKUP_TABLES } from '../analytics/lake-chart-tables';
import { isRecord } from '../is-record';
import { ANALYTICS_COLUMNS } from './analytics-columns';
// Shows Via what an organization's analytics data holds before it writes SQL: fields inside the JSON columns and the
// tools agents call differ per organization and per agent. Everything is aggregated in the database and bounded to
// recent rows, since it runs in the user's browser.
const TABLES = { calls: 'strada_calls_history', chats: 'strada_chats_history' };
const CATEGORY_COLUMNS = {
    calls: ['type', 'ended_reason', 'ended_by', 'campaign_name', 'qa_sentiment'],
    chats: ['status', 'qa_sentiment']
};
const JSON_COLUMNS = {
    calls: ['analysis', 'call_variables', 'session_variables', 'rubric_outcomes', 'sip_headers'],
    chats: ['analysis', 'chat_variables', 'session_variables', 'rubric_outcomes', 'metadata']
};
// Rows scanned for JSON keys and for tool use: the most recent ones, so a long date range stays quick.
const RECENT_ROWS = 2000;
const RECENT_TRANSCRIPTS = 500;
const DEFAULT_SAMPLES = 3;
const SAMPLE_MESSAGES = 30;
const topValueSchema = z.object({ value: z.unknown(), rows: z.number() });
export const inspectAnalyticsDataToolSchema = {
    name: 'inspect_analytics_data',
    description: "See what the open dashboard's calls or chats hold before writing SQL: every column and what it means, the most common values of the text columns, the keys and values inside the JSON columns (analysis fields, variables, session variables, QA outcomes), which tools the agents called and what those tools returned, and the latest conversations in brief. Fields and tools differ per agent, so pass agentId when the question is about one agent.",
    inputSchema: z.object({
        table: z.enum(['calls', 'chats']),
        agentId: z.string().optional().describe("Only this agent's conversations"),
        samples: z
            .number()
            .int()
            .min(0)
            .max(10)
            .optional()
            .describe(`How many recent conversations to show in brief; ${DEFAULT_SAMPLES} when left out`)
    }),
    outputSchema: z.object({
        table: z.string(),
        loaded: z.object({
            rows: z.number(),
            testRows: z.number(),
            dateRange: z.object({ from: z.string(), to: z.string() }),
            transcriptsLoaded: z.boolean()
        }),
        agents: z
            .array(z.object({ agentId: z.string(), agentName: z.string().optional(), rows: z.number() }))
            .describe('Every agent with conversations on the dashboard'),
        columns: z.array(z.object({ name: z.string(), type: z.string(), note: z.string() })),
        values: z.record(z.string(), z.array(topValueSchema)).describe('The most common values of text columns'),
        json: z
            .record(z.string(), z.array(z.object({
            key: z.string(),
            types: z.array(z.string()),
            rows: z.number(),
            distinctValues: z.number(),
            top: z.array(topValueSchema)
        })))
            .describe(`Keys inside each JSON column, from the latest ${RECENT_ROWS} conversations`),
        tools: z
            .array(z.object({
            tool: z.string(),
            calls: z.number(),
            conversations: z.number(),
            argumentKeys: z.array(z.string()),
            results: z.number(),
            jsonResults: z.number(),
            resultKeys: z.array(z.object({ key: z.string(), top: z.array(topValueSchema) })),
            textResults: z.array(z.string()).describe('Examples of results that are plain text, not JSON')
        }))
            .describe(`Tools the agents called, from the latest ${RECENT_TRANSCRIPTS} transcripts`),
        samples: z.array(z.record(z.string(), z.unknown())),
        lakeTables: z
            .record(z.string(), z.record(z.string(), z.string()))
            .describe('The narrower tables under the two history views, with their column types'),
        notes: z.array(z.string())
    })
};
const sqlText = (value) => `'${value.replace(/'/g, "''")}'`;
const numberOf = (value) => (typeof value === 'number' ? value : Number(value ?? 0));
const textOf = (value) => (typeof value === 'string' ? value : '');
const recordOf = (value) => (isRecord(value) ? value : {});
const listOf = (value) => (Array.isArray(value) ? value : []);
const topValues = (value) => listOf(value).map((entry) => ({ value: recordOf(entry).value, rows: numberOf(recordOf(entry).rows) }));
const shorten = (text, length) => (text.length > length ? `${text.slice(0, length)}…` : text);
// Long JSON values are shortened so they show a field's format without flooding the result.
const shownValue = (column, length) => `CASE WHEN jsonb_typeof(${column}) IN ('object', 'array') THEN to_jsonb('(' || jsonb_typeof(${column}) || ')') WHEN length(${column} #>> '{}') > ${length} THEN to_jsonb(left(${column} #>> '{}', ${length}) || '…') ELSE ${column} END`;
const jsonKeysSql = (table, column, where) => `
  WITH recent AS (
    SELECT ${column} AS doc FROM ${table}
    WHERE ${where} AND jsonb_typeof(${column}) = 'object'
    ORDER BY created_at DESC LIMIT ${RECENT_ROWS}
  ),
  pairs AS (SELECT entry.key, entry.value FROM recent, jsonb_each(recent.doc) AS entry),
  keys AS (
    SELECT key, COUNT(*)::int AS rows, COUNT(DISTINCT value)::int AS distinct_values,
      array_agg(DISTINCT jsonb_typeof(value)) AS types
    FROM pairs GROUP BY key
  ),
  counted AS (
    SELECT key, ${shownValue('value', 80)} AS shown, COUNT(*)::int AS rows FROM pairs GROUP BY 1, 2
  ),
  ranked AS (
    SELECT *, row_number() OVER (PARTITION BY key ORDER BY rows DESC, shown::text) AS rank FROM counted
  )
  SELECT keys.key, keys.rows, keys.distinct_values, keys.types,
    jsonb_agg(jsonb_build_object('value', ranked.shown, 'rows', ranked.rows) ORDER BY ranked.rank) AS top
  FROM keys JOIN ranked ON ranked.key = keys.key AND ranked.rank <= 5
  GROUP BY keys.key, keys.rows, keys.distinct_values, keys.types
  ORDER BY keys.rows DESC, keys.key LIMIT 40`;
// Every tool call and every tool result in the recent transcripts. A call's transcript is an array whose tool_calls
// messages list the calls and whose tool_call_result messages hold each result; a chat's is {messages: [...]} with
// one tool message per call holding both.
const toolMessagesSql = (tableKey, where) => tableKey === 'calls'
    ? `WITH recent AS (
        SELECT call_id AS id, transcript FROM strada_calls_history
        WHERE ${where} AND jsonb_typeof(transcript) = 'array'
        ORDER BY created_at DESC LIMIT ${RECENT_TRANSCRIPTS}
      ),
      messages AS (SELECT recent.id, message FROM recent, jsonb_array_elements(recent.transcript) AS message),
      invocations AS (
        SELECT messages.id, invocation->'function'->>'name' AS tool, invocation->'function'->>'arguments' AS arguments
        FROM messages, jsonb_array_elements(
          CASE WHEN jsonb_typeof(message->'toolCalls') = 'array' THEN message->'toolCalls' ELSE '[]'::jsonb END
        ) AS invocation
        WHERE message->>'role' = 'tool_calls'
      ),
      results AS (
        SELECT message->>'name' AS tool, message->>'result' AS result FROM messages
        WHERE message->>'role' = 'tool_call_result'
      )`
    : `WITH recent AS (
        SELECT chat_id AS id, transcript FROM strada_chats_history
        WHERE ${where} AND jsonb_typeof(transcript->'messages') = 'array'
        ORDER BY created_at DESC LIMIT ${RECENT_TRANSCRIPTS}
      ),
      messages AS (
        SELECT recent.id, message FROM recent, jsonb_array_elements(recent.transcript->'messages') AS message
      ),
      invocations AS (
        SELECT id, message->>'toolName' AS tool, message->>'toolArguments' AS arguments FROM messages
        WHERE message->>'role' = 'tool'
      ),
      results AS (
        SELECT message->>'toolName' AS tool, message->>'toolResult' AS result FROM messages
        WHERE message->>'role' = 'tool' AND message ? 'toolResult'
      )`;
// Tool arguments and results are text; only text that parses is read as JSON, since a failed cast stops the query.
const asJsonObject = (column) => `CASE WHEN pg_input_is_valid(${column}, 'jsonb') THEN CASE WHEN jsonb_typeof(${column}::jsonb) = 'object' THEN ${column}::jsonb END END`;
const inspectTools = async (query, tableKey, where) => {
    const messages = toolMessagesSql(tableKey, where);
    const parsed = `${messages}, parsed AS (SELECT tool, result, ${asJsonObject('result')} AS doc FROM results)`;
    const [uses, argumentKeys, results, resultKeys] = await Promise.all([
        query(`${messages}
      SELECT tool, COUNT(*)::int AS calls, COUNT(DISTINCT id)::int AS conversations FROM invocations
      GROUP BY tool ORDER BY calls DESC LIMIT 30`),
        query(`${messages}
      SELECT tool, array_agg(DISTINCT key ORDER BY key) AS keys
      FROM invocations, jsonb_object_keys(COALESCE(${asJsonObject('arguments')}, '{}'::jsonb)) AS key
      GROUP BY tool`),
        query(`${parsed}
      SELECT tool, COUNT(*)::int AS results, (COUNT(*) FILTER (WHERE doc IS NOT NULL))::int AS json_results,
        (array_agg(left(result, 160)) FILTER (WHERE doc IS NULL AND result IS NOT NULL))[1:2] AS text_results
      FROM parsed GROUP BY tool`),
        query(`${parsed},
      pairs AS (SELECT tool, entry.key, entry.value FROM parsed, jsonb_each(COALESCE(doc, '{}'::jsonb)) AS entry),
      counted AS (SELECT tool, key, ${shownValue('value', 60)} AS shown, COUNT(*)::int AS rows FROM pairs GROUP BY 1, 2, 3),
      ranked AS (
        SELECT *, row_number() OVER (PARTITION BY tool, key ORDER BY rows DESC, shown::text) AS rank FROM counted
      )
      SELECT tool, key, jsonb_agg(jsonb_build_object('value', shown, 'rows', rows) ORDER BY rank) AS top
      FROM ranked WHERE rank <= 5 GROUP BY tool, key ORDER BY tool, key LIMIT 300`)
    ]);
    const byTool = (rows, pick) => (tool) => rows.filter((row) => row.tool === tool).map(pick);
    const argumentsOf = byTool(argumentKeys.rows, (row) => listOf(row.keys).map(textOf));
    const resultsOf = byTool(results.rows, (row) => row);
    const resultKeysOf = byTool(resultKeys.rows, (row) => ({ key: textOf(row.key), top: topValues(row.top) }));
    return uses.rows.map((use) => {
        const tool = textOf(use.tool);
        const [result] = resultsOf(tool);
        return {
            tool,
            calls: numberOf(use.calls),
            conversations: numberOf(use.conversations),
            argumentKeys: argumentsOf(tool).flat(),
            results: numberOf(result?.results),
            jsonResults: numberOf(result?.json_results),
            resultKeys: resultKeysOf(tool),
            textResults: listOf(result?.text_results).map(textOf)
        };
    });
};
const briefCallMessage = (message) => {
    switch (message.role) {
        case 'user':
        case 'bot':
            return [`${message.role}: ${shorten(textOf(message.message), 120)}`];
        case 'tool_calls':
            return listOf(message.toolCalls).map((invocation) => {
                const call = recordOf(recordOf(invocation).function);
                return `tool call ${textOf(call.name)}(${shorten(textOf(call.arguments), 120)})`;
            });
        case 'tool_call_result':
            return [`tool result ${textOf(message.name)}: ${shorten(textOf(message.result), 160)}`];
        default:
            return [];
    }
};
const briefChatMessage = (message) => {
    if (message.role === 'tool') {
        return [
            `tool ${textOf(message.toolName)}(${shorten(textOf(message.toolArguments), 120)}) → ${shorten(textOf(message.toolResult), 160)}`
        ];
    }
    return message.role === 'user' || message.role === 'bot' || message.role === 'agent'
        ? [`${message.role}: ${shorten(textOf(message.content), 120)}`]
        : [];
};
const briefTranscript = (tableKey, transcript) => {
    const messages = listOf(tableKey === 'calls' ? transcript : recordOf(transcript).messages).map(recordOf);
    const lines = messages.flatMap(tableKey === 'calls' ? briefCallMessage : briefChatMessage);
    return lines.length > SAMPLE_MESSAGES
        ? [...lines.slice(0, SAMPLE_MESSAGES), `(${lines.length - SAMPLE_MESSAGES} more)`]
        : lines;
};
const briefValue = (value) => {
    if (typeof value === 'string')
        return shorten(value, 200);
    if (Array.isArray(value))
        return value.slice(0, 10).map(briefValue);
    if (typeof value === 'object' && value !== null) {
        return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, briefValue(entry)]));
    }
    return value;
};
const isEmpty = (value) => value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && value !== null && Object.keys(value).length === 0);
// Columns left out of samples: long and of no use for writing SQL.
const SAMPLE_OMITTED = ['recording_url', 'rubric_results'];
const briefSample = (tableKey, row) => Object.fromEntries(Object.entries(row)
    .filter(([column, value]) => !SAMPLE_OMITTED.includes(column) && !isEmpty(value))
    .map(([column, value]) => [
    column,
    column === 'transcript' ? briefTranscript(tableKey, value) : briefValue(value)
]));
/** What the dashboard's calls or chats hold, for Via to read before it writes SQL. */
export const inspectAnalyticsData = async (query, { table: tableKey, agentId, samples = DEFAULT_SAMPLES }, dashboard) => {
    const table = TABLES[tableKey];
    const where = agentId ? `agent_id = ${sqlText(agentId)}` : 'true';
    const overview = await query(`SELECT COUNT(*)::int AS rows, (COUNT(*) FILTER (WHERE is_test))::int AS test_rows FROM ${table} WHERE ${where}`);
    const agents = await query(`SELECT agent_id, max(agent_name) AS agent_name, COUNT(*)::int AS rows FROM ${table}
     GROUP BY agent_id ORDER BY rows DESC LIMIT 20`);
    const categories = await query(CATEGORY_COLUMNS[tableKey]
        .map((column) => `(SELECT ${sqlText(column)} AS column_name, ${column} AS value, COUNT(*)::int AS rows FROM ${table}
            WHERE ${where} GROUP BY ${column} ORDER BY rows DESC LIMIT 10)`)
        .join(' UNION ALL '));
    const json = {};
    for (const column of JSON_COLUMNS[tableKey]) {
        const { rows } = await query(jsonKeysSql(table, column, where));
        json[column] = rows.map((row) => ({
            key: textOf(row.key),
            types: listOf(row.types).map(textOf),
            rows: numberOf(row.rows),
            distinctValues: numberOf(row.distinct_values),
            top: topValues(row.top)
        }));
    }
    const tools = dashboard.transcriptsLoaded ? await inspectTools(query, tableKey, where) : [];
    const recent = samples > 0
        ? await query(`SELECT * FROM ${table} WHERE ${where} ORDER BY created_at DESC LIMIT ${samples}`)
        : { rows: [] };
    const rows = numberOf(overview.rows[0]?.rows);
    const notes = [
        rows === 0 &&
            (agentId
                ? "This agent has no conversations in the dashboard's date range."
                : "The dashboard's date range has no conversations."),
        !dashboard.transcriptsLoaded &&
            'Transcripts are still loading, so tool use is left out and transcript queries would be incomplete. Try again shortly.'
    ].filter((note) => typeof note === 'string');
    return {
        table,
        loaded: {
            rows,
            testRows: numberOf(overview.rows[0]?.test_rows),
            dateRange: { from: dashboard.from, to: dashboard.to },
            transcriptsLoaded: dashboard.transcriptsLoaded
        },
        agents: agents.rows.map((row) => ({
            agentId: textOf(row.agent_id),
            ...(typeof row.agent_name === 'string' && { agentName: row.agent_name }),
            rows: numberOf(row.rows)
        })),
        columns: ANALYTICS_COLUMNS[table],
        values: Object.fromEntries(CATEGORY_COLUMNS[tableKey].map((column) => [
            column,
            categories.rows
                .filter((row) => row.column_name === column)
                .map((row) => ({ value: row.value, rows: numberOf(row.rows) }))
        ])),
        json,
        tools,
        samples: recent.rows.map((row) => briefSample(tableKey, row)),
        lakeTables: { ...LAKE_CHART_TABLES, ...LAKE_LOOKUP_TABLES },
        notes
    };
};
//# sourceMappingURL=analytics-inspect.js.map